import { type FC, useMemo } from 'react';

import { AlertsTable } from '@/components/Studies/Create/Steps/Alerts/AlertsTable';
import { errorToast } from '@/helpers';
import { _notNil } from '@/littledash';
import type { AlertDisplayData, StudyAlertUpdateV1, StudyAlertV1 } from '@/model/Alert.model';
import type { PresetCalculation } from '@/model/PresetCalculation.model';
import type { Study } from '@/model/Study.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useModalAction } from '@/utils/modal';

import type { SuccessfulOverviewSection } from '../../OverviewAccordion.model';
import overviewTableStyles from '../OverviewSectionTable.module.scss';
import { canUpdateOverviewAlert } from '../../overviewSectionAccess';

interface AlertsSectionContentProps {
  section: SuccessfulOverviewSection<'alerts'>;
  study?: Study;
  onAlertSaved?: () => void;
}

type OverviewAlert = SuccessfulOverviewSection<'alerts'>['rows'][number];

const toAlertDisplayData = (alert: OverviewAlert): AlertDisplayData => ({
  id: alert.api_id,
  calculation: alert.metric_id,
  operator: alert.operator,
  value: alert.value,
  type: alert.type,
  from: alert.from ?? null,
  notification: alert.notification,
  action: alert.action ?? null,
  retrigger: alert.retrigger,
});

const toFormAlert = (alert: OverviewAlert) => ({
  api_id: alert.api_id,
  id: alert.api_id,
  name: alert.name ?? '',
  calculation: alert.metric_id,
  operator: alert.operator,
  value: alert.value,
  type: alert.type,
  from: alert.from ?? null,
  notification: alert.notification,
  retrigger: alert.retrigger,
  action: alert.action ?? null,
});

const calculationsFromRows = (rows: Array<OverviewAlert>): Array<PresetCalculation> =>
  Array.from(
    rows.reduce<Map<string, PresetCalculation>>((result, alert) => {
      if (!result.has(alert.metric_id)) {
        result.set(alert.metric_id, {
          id: alert.metric_id,
          name: _notNil(alert.metric_name) ? alert.metric_name : alert.metric_id,
          unit: alert.metric_unit,
          formula: '',
        });
      }
      return result;
    }, new Map())
  ).map(([, calculation]) => calculation);

/**
 * Renders study alerts with the settings Alerts table. The metric opens Edit alert
 * for callers who can edit alerts on an open study.
 */
const AlertsSectionContent: FC<AlertsSectionContentProps> = ({ section, study, onAlertSaved }) => {
  const { openModal, closeModal } = useModalAction();
  const { invoke: updateAlert } = useApiHook({
    endpoint: 'PATCH /api/v1/studies/{studyApiId}/alerts/{studyAlertApiId}',
    options: { invokeOnInit: false, onError: { toast: false, slug: 'overview-alert-update' } },
  });
  const canEditAlert = canUpdateOverviewAlert(study);
  const alerts = useMemo(() => section.rows.map(toAlertDisplayData), [section.rows]);
  const calculations = useMemo(() => {
    const fromStudy = study?.settings?.calculations ?? [];
    if (fromStudy.length > 0) {
      return fromStudy;
    }
    return calculationsFromRows(section.rows);
  }, [section.rows, study]);

  const saveAlert = async (row: OverviewAlert, submitted: StudyAlertV1): Promise<void> => {
    if (!_notNil(study)) {
      return;
    }

    const updateBody: StudyAlertUpdateV1 = {
      calculation: submitted.calculation,
      value: submitted.value,
      operator: submitted.operator,
      type: submitted.type,
      from: submitted.from,
      notification: submitted.notification,
      retrigger: submitted.retrigger,
      action: submitted.action,
      name: row.name ?? submitted.name ?? '',
    };

    try {
      await updateAlert({
        path: { studyApiId: study.api_id, studyAlertApiId: row.api_id },
        body: updateBody,
      });
      closeModal();
      onAlertSaved?.();
    } catch {
      errorToast('Could not update alert');
    }
  };

  const openEditAlert = (display: AlertDisplayData): void => {
    const row = section.rows.find((alert) => alert.api_id === String(display.id));
    if (!_notNil(study) || !_notNil(row)) {
      return;
    }

    openModal('ADD_ALERT_FORM', {
      calculations,
      alert: toFormAlert(row),
      onSubmit: (submitted: StudyAlertV1) => saveAlert(row, submitted),
      onCancel: () => closeModal(),
    });
  };

  return (
    <div data-test-component="AlertsSectionContent" data-test-element="container">
      <AlertsTable
        alerts={alerts}
        calculations={calculations}
        disabled={!canEditAlert}
        onAddAlertClick={() => undefined}
        onAlertClick={openEditAlert}
        maxHeightClassName={overviewTableStyles.maxHeight}
      />
    </div>
  );
};

export default AlertsSectionContent;
